<template>
  <div>
    <b-row>
      <b-col lg="9">
        <b-card>
          <template #header>
            <h4 class="card-title">
              Power Load
            </h4>
          </template>
          <div class="d-flex justify-content-between mb-3">
            <div class="d-flex">
              <div class="ps-3 py-1 bg-white">
                Current Demand
              </div>
              <div class="px-2 py-1 bg-white">
                {{ formatNumber(currentDemand) }} kW
              </div>
            </div>
            <div class="d-flex">
              <div class="ps-3 py-1 bg-white">
                Peak Demand
              </div>
              <div class="px-2 py-1 bg-white">
                {{ formatNumber(peakDemand) }} kW
              </div>
            </div>
          </div>
          <BarChart
            :chart-data="chartData"
            :chart-interval="chartInterval"
            :peak-limit="peakLimit"
            interval="quarter_hourly"
            y-axis-label="Power (kW)"
            x-axis-label="time (hrs.)"
          />
          <div class="d-flex justify-content-between mt-3">
            <div class="d-flex border border-4 border-top-0 border-end-0 border-bottom-0 border-warning shadow-none mb-2">
              <div class="ps-3 py-1 bg-white">
                Alert Level
              </div>
              <div class="px-2 py-1 bg-white">
                {{ alertLevel ? formatNumber(alertLevel) : '-' }} kW
              </div>
            </div>
            <div class="d-flex border border-4 border-top-0 border-start-0 border-bottom-0 border-danger shadow-none mb-2">
              <div class="ps-3 py-1 bg-white">
                Warning Level
              </div>
              <div class="px-2 py-1 bg-white">
                {{ warningLevel ? formatNumber(warningLevel) : '-' }} kW
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col lg="3">
        <div class="d-flex flex-column justify-content-between h-100">
          <b-card class="pb-3">
            <template #header>
              <h4 class="card-title">
                1st - Today
              </h4>
            </template>
            <div class="d-flex mb-4">
              <div class="mx-auto fs-4">
                {{ formatNumber(monthlyData) }} kWh
              </div>
            </div>
            <b-progress
              :max="monthlyLimit"
              :variant="(monthlyDataPercentage >= 95) ? 'danger' : (monthlyDataPercentage >= 90 ? 'warning' : 'lime')"
              height="1.4rem"
              style="border-radius: 5px; background-color: #D5D4D4;"
            >
              <b-progress-bar
                :value="monthlyLimit ? monthlyData : 0"
              >
                <div
                  class="text-white"
                  style="font-size: .8rem; margin-top: 1px;"
                >
                  {{ monthlyDataPercentage }}%
                </div>
              </b-progress-bar>
            </b-progress>
          </b-card>
          <b-card>
            <template #header>
              <h4 class="card-title">
                Daily Energy Accumulation
              </h4>
            </template>
            <div class="d-flex mb-2">
              <div class="mx-auto fs-4">
                {{ formatNumber(dailyData) }} kWh
              </div>
            </div>
            <radial-progress-bar
              class="mx-auto"
              :diameter="150"
              :completed-steps="(dailyData > dailyLimit) ? dailyLimit : dailyData"
              :total-steps="dailyLimit"
              :start-color="(dailyDataPercentage >= 95) ? '#BC250C' : (dailyDataPercentage >= 90 ? '#EDB908' : '#1BB15B')"
              :stop-color="(dailyDataPercentage >= 95) ? '#BC250C' : (dailyDataPercentage >= 90 ? '#EDB908' : '#1BB15B')"
              :inner-stroke-color="'#D5D4D4'"
              :inner-stroke-width="12"
              :stroke-width="12"
            >
              <div class="d-flex flex-column text-primary text-center fs-4">
                <span class="ps-2">
                  {{ dailyDataPercentage }}%
                </span>
                <span class="fs-6">
                  {{ (dailyDataPercentage >= 95) ? 'Level 3' : (dailyDataPercentage >= 90 ? 'Level 2' : 'Level 1') }}
                </span>
              </div>
            </radial-progress-bar>
          </b-card>
        </div>
      </b-col>
      <b-col md="12">
        <b-card>
          <div class="d-md-flex">
            <div class="mb-2 mb-md-0">
              Energy Alarm Notification :
            </div>
            <div class="d-flex">
              <div
                class="ms-md-4 me-md-2 bg-lime my-auto"
                style="width: 50px; height: 10px; border-radius: 5px"
              />
              <div class="ms-2 ms-md-0">
                Level 1 : &lt; 90%
              </div>
            </div>
            <div class="d-flex">
              <div
                class="ms-md-4 me-md-2 bg-warning my-auto"
                style="width: 50px; height: 10px; border-radius: 5px"
              /> <div class="ms-2 ms-md-0">
                Level 2 :  90% - 95%
              </div>
            </div>
            <div class="d-flex">
              <div
                class="ms-md-4 me-md-2 bg-danger my-auto"
                style="width: 50px; height: 10px; border-radius: 5px"
              /> <div class="ms-2 ms-md-0">
                Level 3 : &gt; 95%
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <BuildingDeviceTable
      v-if="isLoggedIn"
      :devices="devices"
    />
  </div>
</template>

<script>
import {
  addDays, eachMinuteOfInterval, format, startOfMonth, isWeekend,
} from 'date-fns';
import RadialProgressBar from 'vue-radial-progress';
import { convertToKwh, formatNumber } from '@/utils/helper';
import BarChart from '@/components/BarChart';
import BuildingDeviceTable from './BuildingDeviceTable.vue';
import { TokenService } from '@/services/local-storage';

export default {
  components: {
    BarChart, RadialProgressBar, BuildingDeviceTable,
  },
  props: {
    energyTarget: {
      type: Number,
      default: 0,
    },
    weekdayLimit: {
      type: Number,
      default: 0,
    },
    weekendLimit: {
      type: Number,
      default: 0,
    },
    updateCounter: {
      type: Number,
      default: 0,
    },
    peakLimit: {
      type: Number,
      default: 0,
    },
    devices: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      payloads: [],
      dailyPayload: [],
      monthlyPayload: [],
    };
  },
  computed: {
    isLoggedIn() {
      return !!TokenService.get();
    },
    buildingId() {
      return this.$route.params.buildingId;
    },
    unitId() {
      return this.$route.params.unitId;
    },
    isWeekend() {
      return isWeekend(new Date());
    },
    dailyLimit() {
      return this.isWeekend ? Number(this.weekendLimit) : Number(this.weekdayLimit);
    },
    monthlyLimit() {
      return this.energyTarget ?? 0;
    },
    chartInterval() {
      const minutesArray = eachMinuteOfInterval({
        start: new Date().setHours(0, 0, 0, 0),
        end: new Date().setHours(23, 59, 0, 0),
      }, { step: 15 });
      return minutesArray.map((item) => format(item, 'HH:mm'));
    },
    todayData() {
      const valueArray = [];
      const todayDate = format(new Date(), 'dd');
      const todayPayloads = this.payloads.filter((payload) => format(new Date(payload.starts_at), 'dd') === todayDate);
      this.chartInterval.forEach((item) => (
        valueArray.push(todayPayloads.find((payload) => format(new Date(payload.starts_at), 'HH:mm') === item))
      ));
      const payloadValue = valueArray.map((item) => (item?.cumulative_value ? Number(convertToKwh(item?.cumulative_value).toFixed(2)) : 0));

      const finalData = [];

      payloadValue.forEach((item) => (
        finalData.push({
          y: item,
          color: this.getChartColor(item),
        })
      ));

      return finalData;
    },
    yesterdayData() {
      const finalData = [];
      const todayDate = format(addDays(new Date(), -1), 'dd');
      const todayPayloads = this.payloads.filter((payload) => format(new Date(payload.starts_at), 'dd') === todayDate);
      this.chartInterval.forEach((item) => (
        finalData.push(todayPayloads.find((payload) => format(new Date(payload.starts_at), 'HH:mm') === item))
      ));
      return finalData.map((item) => (item?.cumulative_value ? Number(convertToKwh(item?.cumulative_value).toFixed(2)) : 0));
    },
    dailyData() {
      if (this.dailyPayload) {
        return Number(convertToKwh(this.dailyPayload[0]?.cumulative_value).toFixed(0));
      }
      return 0;
    },
    monthlyData() {
      if (this.monthlyPayload) {
        return convertToKwh(this.monthlyPayload[0]?.cumulative_value).toFixed(0);
      }
      return 0;
    },
    dailyDataPercentage() {
      if (this.dailyLimit) {
        return Number(((this.dailyData / this.dailyLimit) * 100)).toFixed(2) ?? 0;
      }
      return 0;
    },
    monthlyDataPercentage() {
      if (this.monthlyLimit) {
        return Number(((this.monthlyData / this.energyTarget) * 100)).toFixed(2) ?? 0;
      }
      return 0;
    },
    chartData() {
      return [
        {
          color: '#D5D4D4',
          data: this.yesterdayData,
          name: 'Yesterday',
          enableMouseTracking: false,
          pointPadding: 0,
          pointPlacement: 0,
          groupPadding: 0,
          borderColor: 'white',
        }, {
          color: '#D5D4D4',
          data: this.yesterdayData,
          type: 'spline',
          name: 'Yesterday',
          marker: {
            radius: 0,
            states: {
              hover: {
                fillColor: '#D5D4D4',
                radius: 5,
                radiusPlus: 0,
              },
            },
          },
          tooltip: {
            enabled: false,
          },
          connectNulls: true,
          pointPadding: 0,
          pointPlacement: 0,
          groupPadding: 0,
          borderWidth: 2,
          borderColor: 'white',
        }, {
          // color get from getChartColor
          data: this.todayData,
          name: 'Today',
          pointPadding: 0,
          pointPlacement: 0,
          groupPadding: 0,
          borderColor: 'white',
        }];
    },
    currentDemand() {
      const todayDate = format(new Date(), 'dd');
      const todayPayloads = this.payloads.filter((payload) => format(new Date(payload.starts_at), 'dd') === todayDate);
      const latestData = todayPayloads.reverse().pop();
      return convertToKwh(latestData?.cumulative_value ?? 0);
    },
    peakDemand() {
      if (!this.payloads?.length) {
        return 0;
      }
      const todayDate = format(new Date(), 'dd');
      const todayPayloads = this.payloads.filter((payload) => format(new Date(payload.starts_at), 'dd') === todayDate);
      const flattenData = todayPayloads.map((data) => Number(data.cumulative_value));

      return convertToKwh(Math.max(...flattenData));
    },
    warningLevel() {
      return 0.95 * this.peakLimit;
    },
    alertLevel() {
      return 0.90 * this.peakLimit;
    },
  },
  watch: {
    updateCounter: {
      immediate: true,
      handler() {
        this.getChartData();
        this.getDailyData();
        this.getMonthlyData();
      },
    },
  },
  mounted() {
    this.getChartData();
    this.getDailyData();
    this.getMonthlyData();
  },
  methods: {
    formatNumber,
    getChartColor(value) {
      if (this.peakLimit) {
        if (value >= this.warningLevel) { return '#BC250C'; }
        if (value >= this.alertLevel) { return '#EDB908'; }
      } return '#61C219';
    },
    async getChartData() {
      try {
        this.isLoading = true;
        const { data } = await this.$api.getUnitEnergy(this.unitId,
          {
            per_page: 200,
            block_id: this.buildingId,
            interval: 'quarter_hourly',
            start_date: 'yesterday',
            timezone: 'Asia/Jakarta',
            payload_key: 'Active_Power_Demand',
            aggregate: 'sum',
          });
        this.payloads = data.data;
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getDailyData() {
      try {
        this.isLoading = true;
        const { data } = await this.$api.getUnitEnergy(this.unitId,
          {
            per_page: 200,
            block_id: this.buildingId,
            interval: 'daily',
            start_date: 'today',
            timezone: 'Asia/Jakarta',
            payload_key: 'Active_Energy_Demand_Consumed',
            aggregate: 'sum',
          });
        this.dailyPayload = data.data;
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getMonthlyData() {
      const start = startOfMonth(new Date());
      try {
        this.isLoading = true;
        const { data } = await this.$api.getUnitEnergy(this.unitId,
          {
            per_page: 200,
            block_id: this.buildingId,
            interval: 'monthly',
            start_date: start,
            timezone: 'Asia/Jakarta',
            payload_key: 'Active_Energy_Demand_Consumed',
            aggregate: 'sum',
          });
        this.monthlyPayload = data.data;
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
<style scoped>
.progress-label  {
  -webkit-text-stroke: 2.5px white;
}
</style>
